import translateService from '@widgets/shared/services/translate/translateService'
import { mergeWith } from 'lodash-es'
import {
  WebWidgetChannelType,
  type WebWidgetChatChannel,
  type WebWidgetDetailsAppearance,
  type WebWidgetDetailsCallChannelSettingsUi,
  type WebWidgetDetailsChannelSorting,
  type WebWidgetDetailsChatChannelSettingsUi,
  type WebWidgetDetailsEmailChannelSettingsUi,
  type WebWidgetDetailsFacebookChannelSettingsUi,
  type WebWidgetDetailsInstagramChannelSettingsUi,
  WebWidgetDetailsMinimizedStateAlignTo,
  WebWidgetDetailsMinimizedStateIcon,
  type WebWidgetDetailsSmsChannelSettingsUi,
  type WebWidgetDetailsWhatsappChannelSettingsUi,
  type WebWidgetEmailChannel,
  type WebWidgetParsedChatChannel,
  type WebWidgetParsedEmailChannel,
  WebWidgetThemeColorTypes,
} from '@/data/models/domain/messages/settings/webWidget/types'
import { WebWidgetColorsPaletteSolid } from '@/services/forms/messages/settings/types'

// Shared service for the widget and the widget settings
export class MessengerCommonCastService {
  public getDefaultAppearance(): WebWidgetDetailsAppearance {
    return {
      colorTheme: {
        type: WebWidgetThemeColorTypes.GRADIENT,
        color: WebWidgetColorsPaletteSolid[0].color,
        custom: {
          primaryColor: {
            background: '#008CFF',
            font: '#FFFFFF',
          },
          buttonColor: {
            background: '#008CFF',
            font: '#FFFFFF',
          },
        },
      },
      minimizedState: {
        buttonTextEnabled: false,
        buttonText: '',
        icon: WebWidgetDetailsMinimizedStateIcon.TEXTMAGIC,
        borderRadius: 40,
        alignTo: WebWidgetDetailsMinimizedStateAlignTo.RIGHT,
        sideSpacing: 32,
        bottomSpacing: 26,
        eyeCatcherEnabled: false,
        eyeCatcherText: translateService.t('messengerCastService.minimizedState.eyeCatcherText'),
        eyeCatcherDelay: 7,
      },
      welcomeScreen: {
        greeting: translateService.t('messengerCastService.welcomeScreen.greeting'),
        intro: translateService.t('messengerCastService.welcomeScreen.intro'),
      },
    }
  }

  public parseAppearance(rawAppearance: string): WebWidgetDetailsAppearance {
    return this.parseJsonSettings(rawAppearance, this.getDefaultAppearance())
  }

  // Email channel

  public getDefaultEmailChannelUiSettings(): WebWidgetDetailsEmailChannelSettingsUi {
    return {
      name: translateService.t('messengerCastService.emailChannel.name'),
      mainScreen: {
        title: translateService.t('messengerCastService.emailChannel.mainScreen.title'),
        description: translateService.t('messengerCastService.emailChannel.mainScreen.description'),
        buttonText: translateService.t('messengerCastService.emailChannel.mainScreen.buttonText'),
        fieldSorting: ['name', 'email', 'message'],
      },
      successScreen: {
        title: translateService.t('messengerCastService.emailChannel.successScreen.title'),
        description: translateService.t('messengerCastService.emailChannel.successScreen.description'),
      },
    }
  }

  public parseEmailChannelUiSettings(rawSettings: string) {
    return this.parseJsonSettings(rawSettings, this.getDefaultEmailChannelUiSettings())
  }

  public getDefaultEmailChannel(): WebWidgetParsedEmailChannel {
    return {
      isActive: true,
      email: '',
      inbox: {
        id: '',
        email: '',
      },
      uiSettings: this.getDefaultEmailChannelUiSettings(),
      fieldSettings: {
        name: false,
      },
    }
  }

  public parseEmailChannel(rawEmailChannel: WebWidgetEmailChannel | null): WebWidgetParsedEmailChannel {
    if (!rawEmailChannel) {
      return this.getDefaultEmailChannel()
    }

    return {
      ...rawEmailChannel,
      uiSettings: this.parseEmailChannelUiSettings(rawEmailChannel.uiSettings),
    }
  }

  // SMS channel

  public getDefaultSmsChannelUiSettings(): WebWidgetDetailsSmsChannelSettingsUi {
    return {
      name: translateService.t('messengerCastService.smsChannel.name'),
      showQrCode: true,
      mainScreen: {
        title: translateService.t('messengerCastService.smsChannel.mainScreen.title'),
        description: translateService.t('messengerCastService.smsChannel.mainScreen.description'),
        buttonText: translateService.t('messengerCastService.smsChannel.mainScreen.buttonText'),
      },
      successScreen: {
        title: translateService.t('messengerCastService.smsChannel.successScreen.title'),
        description: translateService.t('messengerCastService.smsChannel.successScreen.description'),
      },
    }
  }

  public parseSmsChannelUiSettings(rawSettings: string) {
    return this.parseJsonSettings(rawSettings, this.getDefaultSmsChannelUiSettings())
  }

  // Call channel

  public getDefaultCallChannelUiSettings(): WebWidgetDetailsCallChannelSettingsUi {
    return {
      name: translateService.t('messengerCastService.callChannel.name'),
      showQrCode: true,
      mainScreen: {
        title: translateService.t('messengerCastService.callChannel.mainScreen.title'),
        description: translateService.t('messengerCastService.callChannel.mainScreen.description'),
        buttonText: translateService.t('messengerCastService.callChannel.mainScreen.buttonText'),
      },
    }
  }

  public parseCallChannelUiSettings(rawSettings: string) {
    return this.parseJsonSettings(rawSettings, this.getDefaultCallChannelUiSettings())
  }

  // Whatsapp channel

  public getDefaultWhatsappChannelUiSettings(): WebWidgetDetailsWhatsappChannelSettingsUi {
    return {
      name: translateService.t('messengerCastService.whatsappChannel.name'),
      showQrCode: true,
      mainScreen: {
        title: translateService.t('messengerCastService.whatsappChannel.mainScreen.title'),
        description: translateService.t('messengerCastService.whatsappChannel.mainScreen.description'),
        buttonText: translateService.t('messengerCastService.whatsappChannel.mainScreen.buttonText'),
      },
    }
  }

  public parseWhatsappChannelUiSettings(rawSettings: string) {
    return this.parseJsonSettings(rawSettings, this.getDefaultWhatsappChannelUiSettings())
  }

  // Facebook channel

  public getDefaultFacebookChannelUiSettings(): WebWidgetDetailsFacebookChannelSettingsUi {
    return {
      name: translateService.t('messengerCastService.facebookChannel.name'),
      showQrCode: true,
      mainScreen: {
        title: translateService.t('messengerCastService.facebookChannel.mainScreen.title'),
        description: translateService.t('messengerCastService.facebookChannel.mainScreen.description'),
        buttonText: translateService.t('messengerCastService.facebookChannel.mainScreen.buttonText'),
      },
    }
  }

  public parseFacebookChannelUiSettings(rawSettings: string) {
    return this.parseJsonSettings(rawSettings, this.getDefaultFacebookChannelUiSettings())
  }

  // Instagram channel

  public getDefaultInstagramChannelUiSettings(): WebWidgetDetailsInstagramChannelSettingsUi {
    return {
      name: translateService.t('messengerCastService.instagramChannel.name'),
      showQrCode: true,
      mainScreen: {
        title: translateService.t('messengerCastService.instagramChannel.mainScreen.title'),
        description: translateService.t('messengerCastService.instagramChannel.mainScreen.description'),
        buttonText: translateService.t('messengerCastService.instagramChannel.mainScreen.buttonText'),
      },
    }
  }

  public parseInstagramChannelUiSettings(rawSettings: string) {
    return this.parseJsonSettings(rawSettings, this.getDefaultInstagramChannelUiSettings())
  }

  // Chat channel

  public getDefaultChatChannelUiSettings(): WebWidgetDetailsChatChannelSettingsUi {
    return {
      name: translateService.t('messengerCastService.chatChannel.name'),
      fieldLabel: translateService.t('messengerCastService.chatChannel.fieldLabel'),
      buttonLabel: translateService.t('messengerCastService.chatChannel.buttonText'),
      preChatScreen: {
        title: translateService.t('messengerCastService.chatChannel.preChatScreen.title'),
        buttonText: translateService.t('messengerCastService.chatChannel.buttonText'),
        fieldSorting: ['name', 'email'],
      },
      offlineMessage: translateService.t('messengerCastService.chatChannel.offlineMessage'),
    }
  }

  public parseChatChannelUiSettings(rawSettings: string) {
    return this.parseJsonSettings(rawSettings, this.getDefaultChatChannelUiSettings())
  }

  public getDefaultChatChannel(): WebWidgetParsedChatChannel {
    return {
      isActive: false,
      uiSettings: this.getDefaultChatChannelUiSettings(),
      fieldSettings: {
        name: false,
      },
      autoReplyMessage: translateService.t('messengerCastService.chatChannel.queue.autoReplyMessage'),
      rejectTimeout: 300,
      rejectMessage: translateService.t('messengerCastService.chatChannel.queue.rejectMessage'),
      isSatisfactionSurveyShown: false,
      isHidden: false,
    }
  }

  public parseChatChannel(rawChatChannel: WebWidgetChatChannel | null): WebWidgetParsedChatChannel {
    const defaultChatChannel = this.getDefaultChatChannel()

    if (!rawChatChannel) {
      return defaultChatChannel
    }

    return {
      ...rawChatChannel,
      autoReplyMessage: rawChatChannel.autoReplyMessage || defaultChatChannel.autoReplyMessage,
      rejectMessage: rawChatChannel.rejectMessage || defaultChatChannel.rejectMessage,
      rejectTimeout: rawChatChannel.rejectTimeout || defaultChatChannel.rejectTimeout,
      uiSettings: this.parseChatChannelUiSettings(rawChatChannel.uiSettings),
      isSatisfactionSurveyShown: Boolean(rawChatChannel.isSatisfactionSurveyShown),
      isHidden: Boolean(rawChatChannel.isHidden),
    }
  }

  // Other

  public getDefaultChannelSorting(): WebWidgetDetailsChannelSorting {
    return [
      WebWidgetChannelType.CHAT,
      WebWidgetChannelType.EMAIL,
      WebWidgetChannelType.SMS,
      WebWidgetChannelType.CALL,
      WebWidgetChannelType.WHATSAPP,
      WebWidgetChannelType.FACEBOOK,
      WebWidgetChannelType.INSTAGRAM,
    ]
  }

  public parseChannelSorting(rawChannelSorting: WebWidgetDetailsChannelSorting): WebWidgetDetailsChannelSorting {
    const defaultChannelSorting = this.getDefaultChannelSorting()

    if (!rawChannelSorting.length) return defaultChannelSorting

    // Filter valid channels
    const filteredChannels: WebWidgetDetailsChannelSorting = rawChannelSorting.filter((item) =>
      defaultChannelSorting.includes(item),
    )

    // Add missing channels
    for (const item of defaultChannelSorting) {
      if (!filteredChannels.includes(item)) {
        filteredChannels.push(item)
      }
    }

    return filteredChannels
  }

  public convertSettingsToJson<T extends object>(settings: T): string {
    return JSON.stringify(settings)
  }

  protected parseJsonSettings<T>(rawSettings: string, defaultSettings: T): T {
    try {
      const parsedSettings = JSON.parse(rawSettings)
      return mergeWith(defaultSettings, parsedSettings, (_objValue, srcValue) => {
        // Don't merge arrays, we need replace it
        return Array.isArray(srcValue) ? srcValue : undefined
      })
    } catch {
      return defaultSettings
    }
  }
}

export default new MessengerCommonCastService()
